import type { InboxItemsResponse } from "@/lib/pb-types";

/**
 * What an inbox item *is*, separately from how it is fetched or drawn.
 *
 * The inbox is the one surface in Kobo whose records did not come from the
 * person looking at them. That single fact is what the shape below is built
 * around:
 *
 * - **`sender` is a string, not a relation.** The people dropping work off have
 *   no account here and are never going to get one — that is the point of the
 *   endpoint. What the app can show is what they typed, and it must be shown as
 *   *their claim* rather than as an identity Kobo verified.
 * - **`receivedAt` is a real instant, not a day key.** Tasks live on a calendar
 *   day because a task is planned; an inbox item happened at a moment, and "18
 *   minutes ago" is the thing worth knowing about it.
 * - **The state machine is three words wide.** `new -> read -> archived`, with
 *   no order field and no day. Deciding what to *do* about an item is the tasks
 *   and kanban surfaces' job; this screen only tracks whether the request has
 *   been looked at and whether it is still live.
 */

export type InboxStatus = "new" | "read" | "archived";

export type InboxItem = {
  id: string;
  title: string;
  description: string;
  /** Who dropped it off, exactly as they typed it. Never blank — the endpoint requires it. */
  sender: string;
  /** Which channel it arrived through: `api` for the endpoint, `app` for typed here. */
  source: string;
  status: InboxStatus;
  /** When the server accepted it. ISO-ish string from PocketBase. */
  receivedAt: string;
  /** When it was first opened, or `null` while it is still unread. */
  readAt: string | null;
  attachments: string[];
  /** PocketBase file URLs need these two, and only the record carries them. */
  collectionId: string;
  collectionName: string;
  /** An optimistic row with no server record yet. */
  pending?: boolean;
};

export const INBOX_STATUSES: readonly InboxStatus[] = ["new", "read", "archived"];

/** Whether an unknown string is one of the three states. */
export function isInboxStatus(value: unknown): value is InboxStatus {
  return typeof value === "string" && (INBOX_STATUSES as readonly string[]).includes(value);
}

/**
 * A PocketBase record as the app's own type.
 *
 * `status` is defended rather than trusted: it is a `select` on the server, but
 * a record hand-edited in the dashboard — or written by a future migration that
 * adds a fourth state — must not render as an empty row. Anything unrecognised
 * reads as `new`, which is the state that keeps it visible.
 */
export function toInboxItem(record: InboxItemsResponse): InboxItem {
  return {
    id: record.id,
    title: record.title,
    description: record.description ?? "",
    sender: record.sender,
    source: record.source ?? "",
    status: isInboxStatus(record.status) ? record.status : "new",
    receivedAt: record.created,
    readAt: record.read_at ? record.read_at : null,
    attachments: record.attachments ?? [],
    collectionId: record.collectionId,
    collectionName: record.collectionName,
  };
}

/* -------------------------------------------------------------------------- */
/* Filtering and counting                                                      */
/* -------------------------------------------------------------------------- */

/** The two things the filter can be set to. `all` means new + read (not archived). */
export type InboxFilter = "all" | "archived";

/**
 * The filter values, in the order the segmented control shows them.
 *
 * Exported as a `const` tuple so the URL parser (`oneOf(INBOX_FILTERS)`) and the
 * control are fed from one list.
 */
export const INBOX_FILTERS = ["all", "archived"] as const;

export function isInboxFilter(value: unknown): value is InboxFilter {
  return value === "all" || value === "archived";
}

/**
 * The items a filter shows.
 *
 * `all` is the inbox: new + read, not archived. The "Archived" tab is the only
 * way to reach archived items.
 */
export function filterByStatus(items: readonly InboxItem[], filter: InboxFilter): InboxItem[] {
  return filter === "all"
    ? items.filter((item) => item.status !== "archived")
    : items.filter((item) => item.status === "archived");
}

/** Free-text match over the title, the description and the sender. */
export function filterByQuery(items: readonly InboxItem[], query: string): InboxItem[] {
  const needle = query.trim().toLowerCase();
  if (needle === "") {
    return [...items];
  }
  return items.filter((item) =>
    `${item.title} ${item.description} ${item.sender}`.toLowerCase().includes(needle),
  );
}

/** How many items sit in each state. Drives the badge on the filter. */
export function countByStatus(items: readonly InboxItem[]): Record<InboxStatus, number> {
  const counts: Record<InboxStatus, number> = { new: 0, read: 0, archived: 0 };
  for (const item of items) {
    counts[item.status] += 1;
  }
  return counts;
}

/**
 * Newest first.
 *
 * The inbox has no manual order and never will: an order the owner curates is
 * what the kanban board is for, and a list somebody else writes into has to
 * put the thing that just arrived where it can be seen.
 */
export function sortByReceived(items: readonly InboxItem[]): InboxItem[] {
  return [...items].sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));
}

/* -------------------------------------------------------------------------- */
/* Presentation                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Parse a PocketBase datetime.
 *
 * PocketBase stores `2026-08-05 08:26:52.114Z` — a space where ISO 8601 wants a
 * `T`. Safari's `Date` refuses that string outright and answers `Invalid Date`,
 * which is how a perfectly good timestamp becomes `NaN minutes ago` in the
 * Tauri shell (WKWebView) while looking fine in Chrome. Normalise, then parse.
 */
export function parseInstant(value: string): Date | null {
  if (!value) {
    return null;
  }
  const date = new Date(value.trim().replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * "just now" / "18 min ago" / "3 h ago" / "5 Aug" — how long ago it landed.
 *
 * Relative up to a week, absolute after that, because "43 days ago" is a number
 * nobody converts into a date. Written by hand rather than with
 * `Intl.RelativeTimeFormat` so the crossover points are visible and testable in
 * the repo's `environment: "node"` setup.
 */
export function receivedLabel(value: string, now: Date = new Date()): string {
  const at = parseInstant(value);
  if (!at) {
    return "";
  }

  const seconds = Math.round((now.getTime() - at.getTime()) / 1000);
  // A clock skew between the server and this machine can make a fresh item
  // arrive "in the future". It is still the newest thing on the screen.
  if (seconds < 60) {
    return "just now";
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} h ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return days === 1 ? "yesterday" : `${days} days ago`;
  }
  return at.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: at.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
}

/** The full timestamp, for a tooltip and for the peek's subtitle. */
export function receivedExact(value: string): string {
  const at = parseInstant(value);
  return at ? at.toLocaleString() : "";
}

/** How the source reads in the UI. Unknown channels are shown verbatim. */
export function sourceLabel(source: string): string {
  switch (source) {
    case "api":
      return "via API";
    case "app":
      return "added here";
    case "":
      return "";
    default:
      return `via ${source}`;
  }
}

/** The word on the status chip. */
export function statusLabel(status: InboxStatus): string {
  switch (status) {
    case "new":
      return "New";
    case "read":
      return "Read";
    case "archived":
      return "Archived";
  }
}
